import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SecurityService {

  private baseUrl: string;

  constructor(private httpClient: HttpClient) {
    this.baseUrl = environment.BASE_LA;
  }

  /**
   * Consulta el estado de 2FA usando un token específico (sin guardarlo en localStorage)
   * @param token Token JWT a usar para la consulta
   */
  get2FAStatusWithToken = async (token: string) => {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('authorization', token);

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/auth/2fa/status`,
          { headers: httpHeaders }
        )
      );
    } catch (error) {
      console.error('Error en get2FAStatusWithToken:', error);
      throw error;
    }
  }

  /**
   * Inicia el proceso de configuración de 2FA usando un token temporal (primer login)
   * NO guarda el token en localStorage, envía tempToken en body
   * @param tempToken Token temporal del login
   */
  setup2FAWithToken = async (tempToken: string) => {
    console.log('🔐 setup2FAWithToken - tempToken recibido:', tempToken ? 'OK' : 'MISSING');

    if (!tempToken) {
      throw new Error('Token no proporcionado a setup2FAWithToken');
    }

    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('X-Skip-Auth-Interceptor', 'true');

    const body = { tempToken };

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/auth/2fa/setup-with-token`,
          body,
          { headers: httpHeaders }
        )
      );
    } catch (error) {
      console.error('❌ Error en setup2FAWithToken:', error);
      throw error;
    }
  }

  /**
   * Inicia el proceso de configuración de 2FA
   * Genera QR code y secreto
   */
  setup2FA = async () => {
    const token = localStorage.getItem('x-token');

    if (!token) {
      throw new Error('No hay sesión activa');
    }

    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('authorization', token);

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/auth/2fa/setup`,
          {},
          { headers: httpHeaders }
        )
      );
    } catch (error) {
      console.error('Error en setup2FA:', error);
      throw error;
    }
  }

  /**
   * Verifica el código TOTP y activa 2FA usando un token específico
   * NO guarda el token en localStorage
   * @param code Código de 6 dígitos
   * @param tempToken Token temporal del login
   */
  verifySetup2FAWithToken = async (code: string, tempToken: string) => {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('X-Skip-Auth-Interceptor', 'true');

    const body = { 
      token: code,
      tempToken: tempToken
    };

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/auth/2fa/verify-setup-with-token`,
          body,
          { headers: httpHeaders }
        )
      );
    } catch (error) {
      console.error('Error en verifySetup2FAWithToken:', error);
      throw error;
    }
  }

  /**
   * Verifica el código TOTP y activa 2FA
   * @param token Código de 6 dígitos
   */
  verifySetup2FA = async (token: string) => {
    const authToken = localStorage.getItem('x-token');

    if (!authToken) {
      throw new Error('No hay sesión activa');
    }

    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('authorization', authToken);

    const body = { token };

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/auth/2fa/verify-setup`,
          body,
          { headers: httpHeaders }
        )
      );
    } catch (error) {
      console.error('Error en verifySetup2FA:', error);
      throw error;
    }
  }

  /**
   * Configurar SMS 2FA con token temporal (primer login)
   * @param tempToken Token temporal del login
   * @param phoneNumber Número de teléfono del usuario
   */
  setupSMS2FAWithToken = async (tempToken: string, phoneNumber: string) => {
    const headers = {
      'content-type': 'application/json',
      'X-Skip-Auth-Interceptor': 'true'
    };
    const body = { tempToken, phoneNumber };

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/auth/2fa-sms/setup-with-token`,
          body,
          { headers }
        )
      );
    } catch (error) {
      console.error('Error en setupSMS2FAWithToken:', error);
      throw error;
    }
  }

  /**
   * Verificar código SMS y activar 2FA con token temporal (primer login)
   * @param tempToken Token temporal del login
   * @param code Código SMS de 6 dígitos
   */
  verifySetupSMS2FAWithToken = async (tempToken: string, code: string) => {
    const headers = {
      'content-type': 'application/json',
      'X-Skip-Auth-Interceptor': 'true'
    };
    const body = { tempToken, code };

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/auth/2fa-sms/verify-setup-with-token`,
          body,
          { headers }
        )
      );
    } catch (error) {
      console.error('Error en verifySetupSMS2FAWithToken:', error);
      throw error;
    }
  }

  /**
   * Verifica código 2FA durante el login
   * @param tempToken Token temporal del login
   * @param token Código de 6 dígitos o código de respaldo
   * @param useBackupCode Si es true, usa código de respaldo
   */
  verifyLogin2FA = async (tempToken: string, token: string, useBackupCode: boolean = false, trustDevice: boolean = false) => {
    const headers = {
      'content-type': 'application/json',
      'X-Skip-Auth-Interceptor': 'true'
    };
    const body = { tempToken, token, useBackupCode, trustDevice };

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/auth/2fa/verify-login`,
          body,
          { headers }
        )
      );
    } catch (error) {
      console.error('Error en verifyLogin2FA:', error);
      throw error;
    }
  }

  /**
   * Verificar código SMS durante login
   * @param tempToken Token temporal del login
   * @param code Código SMS de 6 dígitos
   */
  verifyLoginSMS2FA = async (tempToken: string, code: string, trustDevice: boolean = false) => {
    const headers = {
      'content-type': 'application/json',
      'X-Skip-Auth-Interceptor': 'true'
    };
    const body = { tempToken, code, trustDevice };

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/auth/2fa-sms/verify-login`,
          body,
          { headers }
        )
      );
    } catch (error) {
      console.error('Error en verifyLoginSMS2FA:', error);
      throw error;
    }
  }

  /**
   * Solicitar código SMS durante login
   * @param tempToken Token temporal del login
   */
  requestSMSCode = async (tempToken: string) => {
    const headers = {
      'content-type': 'application/json',
      'X-Skip-Auth-Interceptor': 'true'
    };
    const body = { tempToken };

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/auth/2fa-sms/request-code`,
          body,
          { headers }
        )
      );
    } catch (error) {
      console.error('Error en requestSMSCode:', error);
      throw error;
    }
  }

  /**
   * Desactiva 2FA
   * @param password Contraseña actual
   * @param token Código 2FA actual
   */
  disable2FA = async (password: string, token: string) => {
    const tokenAuth = localStorage.getItem('x-token');

    if (!tokenAuth) {
      throw new Error('No hay sesión activa');
    }

    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('authorization', tokenAuth);

    const body = { password, token };

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/auth/2fa/disable`,
          body,
          { headers: httpHeaders }
        )
      );
    } catch (error) {
      console.error('Error en disable2FA:', error);
      throw error;
    }
  }

  /**
   * Consulta el estado de 2FA del usuario actual
   */
  get2FAStatus = async () => {
    const token = localStorage.getItem('x-token');

    if (!token) {
      throw new Error('No hay sesión activa');
    }

    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('authorization', token);

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/auth/2fa/status`,
          { headers: httpHeaders }
        )
      );
    } catch (error) {
      console.error('Error en get2FAStatus:', error);
      throw error;
    }
  }

  /**
   * Regenera códigos de respaldo
   * @param token Código 2FA actual
   */
  regenerateBackupCodes = async (token: string) => {
    const headers = { 'content-type': 'application/json' };
    const authToken = localStorage.getItem('x-token');

    if (!authToken) {
      throw new Error('No hay sesión activa');
    }

    const httpHeaders = new HttpHeaders({
      'content-type': 'application/json',
      'x-token': authToken
    });

    const body = { token };

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/auth/2fa/regenerate-backup-codes`,
          body,
          { headers: httpHeaders }
        )
      );
    } catch (error) {
      console.error('Error en regenerateBackupCodes:', error);
      throw error;
    }
  }

  /**
   * Desactiva 2FA usando código de respaldo
   * @param password Contraseña actual
   * @param backupCode Código de respaldo
   */
  disable2FAWithBackup = async (password: string, backupCode: string) => {
    const headers = { 'content-type': 'application/json' };
    const authToken = localStorage.getItem('x-token');

    if (!authToken) {
      throw new Error('No hay sesión activa');
    }

    const httpHeaders = new HttpHeaders({
      'content-type': 'application/json',
      'x-token': authToken
    });

    const body = { password, backupCode };

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/auth/2fa/disable-with-backup`,
          body,
          { headers: httpHeaders }
        )
      );
    } catch (error) {
      console.error('Error en disable2FAWithBackup:', error);
      throw error;
    }
  }

  // === Admin: Trusted Devices ===

  /**
   * Obtener dispositivos de confianza de un usuario (admin)
   */
  getAdminUserTrustedDevices = async (userId: string) => {
    const authToken = localStorage.getItem('x-token');
    if (!authToken) throw new Error('No hay sesión activa');

    const headers = new HttpHeaders({
      'content-type': 'application/json',
      'x-token': authToken
    });

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/admin/security/user/${userId}/trusted-devices`,
          { headers }
        )
      );
    } catch (error) {
      console.error('Error en getAdminUserTrustedDevices:', error);
      throw error;
    }
  }

  /**
   * Revocar un dispositivo de confianza específico (admin)
   */
  adminRevokeTrustedDevice = async (userId: string, deviceId: string) => {
    const authToken = localStorage.getItem('x-token');
    if (!authToken) throw new Error('No hay sesión activa');

    const headers = new HttpHeaders({
      'content-type': 'application/json',
      'x-token': authToken
    });

    try {
      return lastValueFrom(
        this.httpClient.delete<any>(
          `${this.baseUrl}/api/admin/security/user/${userId}/trusted-devices/${deviceId}`,
          { headers }
        )
      );
    } catch (error) {
      console.error('Error en adminRevokeTrustedDevice:', error);
      throw error;
    }
  }

  /**
   * Revocar TODOS los dispositivos de confianza de un usuario (admin)
   */
  adminRevokeAllTrustedDevices = async (userId: string) => {
    const authToken = localStorage.getItem('x-token');
    if (!authToken) throw new Error('No hay sesión activa');

    const headers = new HttpHeaders({
      'content-type': 'application/json',
      'x-token': authToken
    });

    try {
      return lastValueFrom(
        this.httpClient.delete<any>(
          `${this.baseUrl}/api/admin/security/user/${userId}/trusted-devices`,
          { headers }
        )
      );
    } catch (error) {
      console.error('Error en adminRevokeAllTrustedDevices:', error);
      throw error;
    }
  }
}
